import clsx from "clsx";
import { useState } from "react";
import { AlertOctagon, Armchair, ArrowDownRight, ArrowUpRight, ChevronRight, Clock3, Luggage, Plane, Search, Truck } from "lucide-react";
import { operatingDate, type AirportConfig, type Turn } from "@yb/core";
import type { Board } from "../lib/api";
import { airlineColor, delayMin, delayText, hhmm, label, legStatusText, longDate } from "../lib/format";
import { useLocalStorage } from "../lib/hooks";

const MIN = 60_000;

type Tab = "stands" | "counters" | "belts";
const TABS: Array<[Tab, string, React.ReactNode]> = [
  ["stands", "Stands & gates", <Plane key="s" className="size-4" />],
  ["counters", "Check-in", <Armchair key="c" className="size-4" />],
  ["belts", "Reclaim", <Luggage key="b" className="size-4" />],
];

/**
 * The phone view: the day as cards in three parts - stands and gates, check-in counters
 * and reclaim belts - soonest first. Tapping a card opens the flight, where a planner can
 * move it, extend it or cancel it; everyone else reads it.
 */
export function RampView({ board, config, onSelect }: { board: Board; config: AirportConfig; onSelect?: (turnId: string) => void }) {
  const [tab, setTab] = useLocalStorage<Tab>("yb.phone.tab", "stands");
  const [q, setQ] = useState("");
  const wanted = q.trim().toUpperCase().replace(/\s+/g, "");
  const match = (t: Turn) => !wanted || [t.arr?.flightNumber, t.dep?.flightNumber, t.registration].some((x) => x?.toUpperCase().includes(wanted));
  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 border-b border-line bg-panel">
        <div className="flex">
          {TABS.map(([id, name, icon]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={clsx(
                "flex min-h-11 flex-1 items-center justify-center gap-1.5 border-b-2 px-2 text-[13px] font-semibold",
                tab === id ? "border-brand text-ink" : "border-transparent text-ink-3",
              )}
            >
              {icon} {name}
            </button>
          ))}
        </div>
        <label className="mx-3 my-2 flex items-center gap-2 rounded-lg border border-line px-2.5">
          <Search className="size-4 text-ink-3" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a flight or tail"
            className="min-h-10 flex-1 bg-transparent text-[15px] outline-none"
            inputMode="search"
          />
        </label>
      </div>
      <div className="min-h-0 flex-1">
        {tab === "stands" && <StandCards board={board} config={config} match={match} onSelect={onSelect} />}
        {tab === "counters" && <ResourceCards board={board} config={config} kind="counters" match={match} onSelect={onSelect} />}
        {tab === "belts" && <ResourceCards board={board} config={config} kind="belts" match={match} onSelect={onSelect} />}
      </div>
    </div>
  );
}

/** Check-in desks or reclaim belts, one card per flight, by when they open. */
function ResourceCards({
  board,
  config,
  kind,
  match,
  onSelect,
}: {
  board: Board;
  config: AirportConfig;
  kind: "counters" | "belts";
  match: (t: Turn) => boolean;
  onSelect?: (turnId: string) => void;
}) {
  const tz = board.airport.timezone;
  const now = board.now;
  const isToday = board.date === operatingDate(now, tz);
  const rows = board.turns
    .filter(match)
    .flatMap((t) => {
      const leg = kind === "counters" ? t.dep : t.arr;
      if (!leg) return [];
      const a = kind === "counters" ? board.plan.counters[leg.id] : board.plan.belts[leg.id];
      if (!a) return [];
      const where =
        kind === "counters"
          ? (board.plan.counters[leg.id]?.counters ?? []).map((id) => config.counters.find((c) => c.id === id)?.number ?? id).join(", ")
          : (config.belts.find((x) => x.id === board.plan.belts[leg.id]?.beltId)?.name ?? "");
      return [{ t, leg, from: a.from, to: a.to, extended: a.extendedFrom != null, where }];
    })
    .filter((r) => !isToday || r.to >= now - 10 * MIN)
    .sort((a, b) => a.from - b.from)
    .slice(0, 60);
  return (
    <div className="scroll-thin h-full overflow-auto">
      <div className="mx-auto max-w-xl space-y-2 p-3">
        {rows.map(({ t, leg, from, to, extended, where }) => {
          const open = from <= now && to >= now;
          return (
            <button
              key={leg.id}
              onClick={() => onSelect?.(t.id)}
              className={clsx("flex w-full items-stretch overflow-hidden rounded-xl border bg-panel text-left", open ? "border-ok" : "border-line")}
            >
              <div className="w-1.5" style={{ background: airlineColor(t.airline, config) }} />
              <div className="flex-1 p-3">
                <div className="flex items-center gap-2">
                  {kind === "counters" ? <ArrowUpRight className="size-4 text-ink-3" /> : <ArrowDownRight className="size-4 text-ink-3" />}
                  <div className="text-[17px] font-bold">{leg.flightNumber}</div>
                  <div className="text-[12px] text-ink-3">{kind === "counters" ? `to ${leg.otherAirport}` : `from ${leg.otherAirport}`}</div>
                  <div className="ml-auto text-right">
                    <div className="text-[20px] leading-none font-black">{where || "—"}</div>
                    <div className="text-[10px] text-ink-3">{kind === "counters" ? "counters" : "belt"}</div>
                  </div>
                </div>
                <div className="tabular mt-1.5 flex flex-wrap items-center gap-x-3 text-[12.5px] text-ink-2">
                  <span>
                    {kind === "counters" ? "Open" : "Bags"} <b>{hhmm(from, tz)}–{hhmm(to, tz)}</b>
                  </span>
                  {extended && <span className="font-semibold text-warn">Extended</span>}
                  {open && <span className="font-semibold text-ok">Open now</span>}
                  <span className="text-ink-3">
                    {kind === "counters" ? "Departs" : "Lands"} {hhmm(leg.actual ?? leg.estimated ?? leg.scheduled, tz)}{" "}
                    <span className="text-bad">{delayText(delayMin(leg))}</span>
                  </span>
                  {leg.bags != null && <span className="text-ink-3">{leg.bags} bags</span>}
                </div>
              </div>
              {onSelect && <ChevronRight className="my-auto mr-2 size-4 text-ink-3" />}
            </button>
          );
        })}
        {rows.length === 0 && <div className="py-16 text-center text-[13px] text-ink-3">Nothing to show.</div>}
      </div>
    </div>
  );
}

/** Stands and gates: the next turns, soonest first, with stand, times and anything that needs attention. */
function StandCards({ board, config, match, onSelect }: { board: Board; config: AirportConfig; match: (t: Turn) => boolean; onSelect?: (turnId: string) => void }) {
  const tz = board.airport.timezone;
  const now = board.now;
  // Another day's turns are all still to come, so none of them is "next" and none is over.
  const isToday = board.date === operatingDate(now, tz);
  const hardByTurn = new Set(board.conflicts.filter((c) => c.severity === "hard").flatMap((c) => c.turnIds));
  const predByTurn = new Map(board.predictions.map((p) => [p.causeTurnId, p]));
  const upcoming = (isToday ? board.turns.filter((t) => t.end >= now - 10 * MIN) : [...board.turns])
    .filter(match)
    .sort((a, b) => Math.max(a.start, now) - Math.max(b.start, now) || a.start - b.start)
    .slice(0, 40);

  return (
    <div className="scroll-thin h-full overflow-auto">
      <div className="mx-auto max-w-xl space-y-2 p-3">
        <div className="px-1 text-[12px] text-ink-3">
          {isToday ? `Next turns from ${hhmm(now, tz)} · updates live` : `Turns on ${longDate(board.date, tz)}`}
        </div>
        {upcoming.map((t) => {
          const stand = config.stands.find((s) => s.id === board.plan.stands[t.id]?.standId);
          const onBlock = t.arr?.actual != null && t.start <= now && t.end >= now;
          const pred = predByTurn.get(t.id);
          const bad = hardByTurn.has(t.id);
          const startsIn = Math.round((t.start - now) / MIN);
          const belt = t.arr ? config.belts.find((b) => b.id === board.plan.belts[t.arr!.id]?.beltId) : undefined;
          return (
            <button
              key={t.id}
              onClick={() => onSelect?.(t.id)}
              className={clsx("block w-full overflow-hidden rounded-xl border bg-panel text-left", bad ? "border-bad" : pred ? "border-warn" : "border-line")}
            >
              <div className="flex items-stretch">
                <div className="w-1.5" style={{ background: airlineColor(t.airline, config) }} />
                <div className="flex-1 p-3">
                  <div className="flex items-center gap-2">
                    <div className="text-[17px] font-bold">{label(t)}</div>
                    {t.segment && <Truck className="size-4 text-ink-3" />}
                    <div className="ml-auto text-right">
                      <div className="text-[22px] leading-none font-black">{stand?.name.replace("Stand ", "") ?? "—"}</div>
                      <div className="text-[10px] text-ink-3">{stand ? "stand" : "no stand"}</div>
                    </div>
                  </div>
                  <div className="text-[12px] text-ink-2">
                    {t.aircraftType ?? "?"}
                    {t.aircraftTypeInferred && "*"} · {t.registration ?? "no reg"}
                    {onBlock ? (
                      <span className="ml-2 font-semibold text-ok">On stand</span>
                    ) : startsIn > 0 && isToday ? (
                      <span className="ml-2">in {startsIn < 60 ? `${startsIn} min` : `${Math.floor(startsIn / 60)} h ${startsIn % 60} m`}</span>
                    ) : null}
                  </div>
                  <div className="tabular mt-2 grid grid-cols-2 gap-2 text-[12px]">
                    {t.arr ? (
                      <div>
                        <div className="flex items-center gap-1 text-ink-3">
                          <ArrowDownRight className="size-3.5" /> {t.arr.flightNumber} from {t.arr.otherAirport}
                        </div>
                        <div className="text-[15px] font-bold">
                          {hhmm(t.arr.actual ?? t.arr.estimated ?? t.arr.scheduled, tz)}{" "}
                          <span className="text-[11px] text-bad">{delayText(delayMin(t.arr))}</span>
                        </div>
                        <div className="text-[11px] text-ink-3">
                          {legStatusText(t.arr)}
                          {belt && ` · ${belt.name}`}
                        </div>
                      </div>
                    ) : (
                      <div className="text-ink-3">Towed in / positioned</div>
                    )}
                    {t.dep ? (
                      <div>
                        <div className="flex items-center gap-1 text-ink-3">
                          <ArrowUpRight className="size-3.5" /> {t.dep.flightNumber} to {t.dep.otherAirport}
                        </div>
                        <div className="text-[15px] font-bold">
                          {hhmm(t.dep.actual ?? t.dep.estimated ?? t.dep.scheduled, tz)}{" "}
                          <span className="text-[11px] text-bad">{delayText(delayMin(t.dep))}</span>
                        </div>
                        <div className="text-[11px] text-ink-3">{legStatusText(t.dep)}</div>
                      </div>
                    ) : (
                      <div className="text-ink-3">Towed off after arrival</div>
                    )}
                  </div>
                  {bad && (
                    <div className="mt-2 flex items-center gap-1.5 rounded-md bg-bad/10 px-2 py-1 text-[12px] font-semibold text-bad">
                      <AlertOctagon className="size-3.5" /> Stand conflict — planner is on it
                    </div>
                  )}
                  {!bad && pred && (
                    <div className="mt-2 flex items-center gap-1.5 rounded-md bg-warn/10 px-2 py-1 text-[12px] font-semibold text-warn">
                      <Clock3 className="size-3.5" /> Likely {pred.causeDelayMin} min late ({Math.round(pred.probability * 100)}%)
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
        {upcoming.length === 0 && <div className="py-16 text-center text-[13px] text-ink-3">{isToday ? "No more turns today." : "No turns that day."}</div>}
      </div>
    </div>
  );
}
